import { useEffect, useState } from "react";
import { Crown, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, LEVEL_LABEL } from "@/lib/api";

export const LevelPicker = ({ value, onChange, testid }) => (
  <div className="inline-flex p-0.5 border border-white/15 rounded-full" role="radiogroup" data-testid={testid}>
    {["free", "base", "pro"].map((l) => (
      <button key={l} type="button" role="radio" aria-checked={value === l} onClick={() => value !== l && onChange(l)} data-testid={`${testid}-${l}`}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${value === l ? (l === "pro" ? "bg-[#F59E0B] text-[#0B0F17]" : l === "base" ? "bg-sky-400 text-[#0B0F17]" : "bg-[#F5F1E8] text-[#0B0F17]") : "text-[#F5F1E8]/70 hover:text-white"}`}>
        {l === "free" ? "Gratis" : l === "base" ? "Base $500" : "Pro $800"}
      </button>
    ))}
  </div>
);

export const AdminClients = () => {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => setUsers([]));
  }, []);

  const setLevel = async (u, v) => {
    const prev = u.subscription_level;
    setUsers((all) => all.map((x) => (x.id === u.id ? { ...x, subscription_level: v } : x)));
    try {
      await api.patch(`/admin/users/${u.id}`, { subscription_level: v });
      toast.success(`${u.name}: ${LEVEL_LABEL[v]}`);
    } catch (e) {
      setUsers((all) => all.map((x) => (x.id === u.id ? { ...x, subscription_level: prev } : x)));
      toast.error(formatApiError(e));
    }
  };

  const term = q.trim().toLowerCase();
  const list = (users ?? []).filter((u) => !term || [u.name, u.email, u.company].some((s) => s?.toLowerCase().includes(term)));

  return (
    <section className="mt-10" data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-[#F5F1E8]/70 max-w-lg">Asigna el plan de cada cliente: Gratis (Diagnóstico Exprés), Base $500 MXN/mes (NOM-002, NOM-019 e ISO 9001 base) o Pro $800 MXN/mes (todo el catálogo, incluidas las Inspecciones Exhaustivas).</p>
        <label className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#F59E0B] transition-colors py-2 sm:w-72">
          <Search className="w-4 h-4 text-[#F5F1E8]/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente" data-testid="admin-clients-search" className="bg-transparent outline-none flex-1 text-sm" />
        </label>
      </div>
      <div className="mt-6 border-t border-white/10" data-testid="admin-clients-list">
        {users === null && <div className="py-12 font-mono text-sm text-[#F5F1E8]/60">Cargando…</div>}
        {users !== null && list.length === 0 && <div className="py-12 text-[#F5F1E8]/60" data-testid="admin-clients-empty">No hay clientes que coincidan.</div>}
        {list.map((u) => (
          <div key={u.id} className="grid grid-cols-12 gap-4 items-center py-5 px-2 border-b border-white/10" data-testid={`admin-client-${u.id}`}>
            <div className="col-span-12 sm:col-span-6 min-w-0">
              <div className="font-semibold truncate">{u.name} {u.company && <span className="text-[#F5F1E8]/55 font-normal">· {u.company}</span>}</div>
              <div className="font-mono text-xs text-[#F5F1E8]/55 truncate">{u.email}</div>
            </div>
            <div className="col-span-5 sm:col-span-2 font-mono text-xs text-[#F5F1E8]/65">{u.audits} auditorías</div>
            <div className="col-span-7 sm:col-span-4 flex items-center justify-end gap-3">
              <Crown className={`w-4 h-4 ${u.subscription_level === "pro" ? "text-[#F59E0B]" : u.subscription_level === "base" ? "text-sky-300" : "text-[#F5F1E8]/30"}`} />
              <LevelPicker value={u.subscription_level ?? "free"} onChange={(v) => setLevel(u, v)} testid={`admin-level-${u.id}`} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
